import type React from 'react'
import { useLayoutEffect, useState } from 'react'

import type { ShapeData } from '@/lib/shapes/types/shape-data'

import type { ScreenPoint } from '../types/screen-rect'

/**
 * The smart-placed screen position of a floating chrome element anchored to `shape`, recomputed
 * (before paint) whenever the shape or zoom changes and on every window scroll/resize. `null`
 * until the first computation — callers render nothing until then.
 *
 * `computePos` must be a stable (module-level) function: it is an effect dependency.
 */
export function useFloatingChromePosition(
  canvasRef: React.RefObject<HTMLDivElement | null>,
  shape: ShapeData,
  scale: number,
  computePos: (shape: ShapeData, scale: number, canvasRect: DOMRect) => ScreenPoint
): ScreenPoint | null {
  const [pos, setPos] = useState<ScreenPoint | null>(null)

  useLayoutEffect(() => {
    const update = () => {
      const el = canvasRef.current

      if (!el) return
      setPos(computePos(shape, scale, el.getBoundingClientRect()))
    }

    update()
    window.addEventListener('scroll', update, true)
    window.addEventListener('resize', update)

    return () => {
      window.removeEventListener('scroll', update, true)
      window.removeEventListener('resize', update)
    }
  }, [canvasRef, shape, scale, computePos])

  return pos
}
